import { useEffect } from "react";

import { trackInputModality } from "@/compat/inputModality";
import { useTranslation } from "react-i18next";
import {
  createRootRoute,
  type ErrorComponentProps,
  HeadContent,
  Outlet,
  ScriptOnce,
  Scripts,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { TooltipProvider } from "@/components/ui/tooltip";
import { THEME_STORAGE_KEY, ThemeProvider } from "@/providers/ThemeProvider";
import { paletteBootstrapScript } from "@/engine/support/palette";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RouteMetaUrls } from "@/components/RouteMetaUrls";
import { currentHost } from "@/providers/currentHost";
import { isCanonicalHost, siteOrigin } from "@/engine/support/siteHost";
import { isMarketingPage } from "@/engine/support/marketingPages";
import { ConsentPreferences } from "@/components/ConsentPreferences";
import { ProfileSync } from "@/components/auth/ProfileSync";
import { ConsentProvider } from "@/hooks/useConsent";
import NotFound from "@/pages/NotFound";
import { reportLovableError } from "@/engine/support/lovable-error-reporting";
import { applyStoredConsent } from "@/engine/diagnostics/consent";
import { ADS_TAG_SRC, ADS_TAG_STUB } from "@/engine/diagnostics/adsConsent";
import { consentModeDefaultScript } from "@/engine/support/consentRegions";
import { isLegalPage } from "@/engine/support/legalPages";
import { installPerfProbes } from "@/engine/diagnostics/perfProbes";
import { hydrationWatchdogScript } from "@/engine/support/hydrationWatchdog";
import { storedSessionScript } from "@/engine/support/storedSession";
import { directionOf } from "@/engine/support/locales";
import { I18nProvider } from "@/providers/I18nProvider";
import { initialLocale } from "@/providers/initialLocale";
import { initialConsent } from "@/providers/initialConsent";
import { HYDRATION_RETRY_KEY } from "@/engine/support/chunkFailure";
import { MEDIA_HOST_ID } from "@/engine/support/mediaHost";
import {
  installableHead,
  registerServiceWorker,
} from "@/engine/support/installableApp";
import appCss from "../styles.css?url";

// ported from main.tsx
if (typeof window !== "undefined") {
  installPerfProbes();
  // A stored, current choice is applied now; an undecided visitor is left for
  // the consent surface to resolve by region (see ConsentProvider).
  applyStoredConsent();
}

// The one face the first paint is drawn in. Every other subset is declared in
// `src/styles.css` and fetched when a page actually needs it; this one is asked
// for in the head, because the largest text on the entry page is set in it and
// a font discovered only once the stylesheet has been parsed arrives after that
// text has already been painted in the fallback (#381).
const OUTFIT_LATIN = "/fonts/outfit-latin.woff2";

// Sets the palette before first paint so SSR pages don't flash the wrong one;
// built from the same list ThemeProvider resolves, with the same fallback.
const themeBootstrap = paletteBootstrapScript(THEME_STORAGE_KEY);

// Consent Mode defaults must be pushed before the tag loads, or they do not
// apply at all. The text is generated from the one region configuration in
// `consentRegions.ts` so it can never drift from the region gate. Google
// resolves the region itself, so the app needs no geo lookup for compliance —
// the consent surface's own lookup is only about when to ask.
const consentDefaults = consentModeDefaultScript();

// The watchdog that recovers a bundle that never arrived; the module holds the
// script, the retry policy and the argument for both.
const hydrationWatchdog = hydrationWatchdogScript();
// Whether an account's session is stored, marked on `<html>` before the first
// paint so home is drawn for the visitor it will turn out to be (#872).
const storedSession = storedSessionScript();

export const Route = createRootRoute({
  head: ({ matches }) => {
    // og:url and canonical must self-reference the page being rendered; the
    // last match's pathname is the current route during SSR.
    const leafPath = matches[matches.length - 1]?.pathname ?? "/";
    const canonicalPath = leafPath === "/" ? "/" : leafPath.replace(/\/+$/, "");
    // Every published page claims the canonical origin and its own path.
    const pageUrl = `${siteOrigin(currentHost())}${canonicalPath}`;
    const installable = installableHead();
    return {
      meta: [
        { charSet: "utf-8" },
        {
          name: "viewport",
          // `viewport-fit=cover` hands the notch and the home indicator to
          // the page, which pads itself by the safe-area insets where it draws.
          content: "width=device-width, initial-scale=1.0, viewport-fit=cover",
        },
        ...installable.meta,
        { title: "SyncAni — Spatial Audio Meetings" },
        {
          name: "description",
          content:
            "Rooms with 3D spatial audio for effective team collaboration",
        },
        { name: "author", content: "SyncAni" },
        { property: "og:title", content: "SyncAni — Spatial Audio Meetings" },
        {
          property: "og:description",
          content:
            "Rooms with 3D spatial audio for effective team collaboration",
        },
        { property: "og:type", content: "website" },
        // `/landing` on a preview is a copy of the canonical root.
        ...(isMarketingPage(leafPath) && !isCanonicalHost(currentHost())
          ? [{ name: "robots", content: "noindex" }]
          : []),
        { property: "og:url", content: pageUrl },
        {
          property: "og:image",
          content: "https://syncani.com/og-image.jpg",
        },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:site", content: "@SyncAni" },
        {
          name: "twitter:title",
          content: "SyncAni — Spatial Audio Meetings",
        },
        {
          name: "twitter:description",
          content:
            "Rooms with 3D spatial audio for effective team collaboration",
        },
        {
          name: "twitter:image",
          content: "https://syncani.com/og-image.jpg",
        },
      ],
      links: [
        ...installable.links,
        // The face and the sheet start together, on the connection this HTML
        // already arrived over — no third-party DNS, TLS or round trip in
        // front of the first paint. `crossOrigin` is still required: a font
        // is fetched in CORS mode whatever origin it is on, and a preload
        // without it warms a response the real request cannot use.
        {
          rel: "preload",
          as: "font",
          type: "font/woff2",
          href: OUTFIT_LATIN,
          crossOrigin: "anonymous",
        },
        { rel: "stylesheet", href: appCss },
        { rel: "canonical", href: pageUrl },
        { rel: "icon", type: "image/png", href: "/favicon.png" },
      ],
    };
  },
  // The locale the whole response is rendered in: read from the request on
  // the server, from the store on the client, and carried to the client as
  // loader data so the first render there is the one the server wrote. The
  // theme cannot do this — nothing is sent about it — which is why it still
  // has a bootstrap script and the language no longer does.
  loader: () => ({ locale: initialLocale(), consent: initialConsent() }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: RootErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  // The shell wraps the root match, error component and not-found component
  // alike, so the catalogue is carried here rather than around the route
  // component: a visitor who lands on a page that does not exist reads that in
  // their own language too.
  const { locale } = Route.useLoaderData();

  return (
    // `suppressHydrationWarning` is for the theme and the stored session: their
    // bootstrap scripts mark this element before React sees it. The language is rendered, not
    // patched, so it needs no forgiveness.
    <I18nProvider locale={locale}>
      <html lang={locale} dir={directionOf(locale)} suppressHydrationWarning>
        <head>
          <HeadContent />
          {/* Run once while the document parses, and never hydrated: React
              matches inline head scripts by position, and Cloudflare's Google
              tag gateway writes inline scripts of its own at the top of the
              head, which would take these scripts' places and send React into
              rendering the whole document again (#418). */}
          <ScriptOnce>{themeBootstrap}</ScriptOnce>
          <ScriptOnce>{storedSession}</ScriptOnce>
          <ScriptOnce>{consentDefaults}</ScriptOnce>
          {/* The stub queues calls until gtag.js loads after hydration. */}
          <ScriptOnce>{ADS_TAG_STUB}</ScriptOnce>
          <ScriptOnce>{hydrationWatchdog}</ScriptOnce>
        </head>
        <body>
          {children}
          {/* Hidden media is appended here, not into <body>. React never
              reconciles this element's children — see support/mediaHost.ts. */}
          <div
            id={MEDIA_HOST_ID}
            style={{
              position: "fixed",
              width: 0,
              height: 0,
              overflow: "hidden",
              pointerEvents: "none",
            }}
          />
          <Scripts />
        </body>
      </html>
    </I18nProvider>
  );
}

function RootComponent() {
  // Everywhere but the legal pages, which are what a visitor reads in order to
  // decide — see `legalPages.ts`.
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const consentSurfaceHere = !isLegalPage(pathname);
  const { consent } = Route.useLoaderData();

  // The page is served rendered, so its buttons exist in the DOM before React
  // has attached anything to them — a click in that window is swallowed. The
  // marker is the only honest signal that the app is live, and the e2e helpers
  // wait for it rather than for an element that was always there.
  useEffect(() => {
    document.documentElement.dataset["hydrated"] = "true";
    // A late arrival still counts: the watchdog's banner and its one-shot
    // retry flag are cleared the moment the app is actually live.
    document.getElementById("hydration-fallback")?.remove();
    try {
      sessionStorage.removeItem(HYDRATION_RETRY_KEY);
    } catch {
      /* private mode */
    }
    if (!document.querySelector(`script[src="${ADS_TAG_SRC}"]`)) {
      // Google Tag Gateway rewrites a server-rendered head before React sees it.
      const googleTag = document.createElement("script");
      googleTag.src = ADS_TAG_SRC;
      googleTag.async = true;
      googleTag.crossOrigin = "anonymous";
      document.head.append(googleTag);
    }
  }, []);

  // Rings and tooltips on focus are for somebody using the keys.
  useEffect(() => trackInputModality(document), []);

  useEffect(() => {
    void registerServiceWorker(
      import.meta.env.PROD,
      "serviceWorker" in navigator ? navigator.serviceWorker : undefined,
    );
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <TooltipProvider>
          <ConsentProvider initial={consent}>
            <RouteMetaUrls />
            <ProfileSync />
            {consentSurfaceHere ? <ConsentPreferences /> : null}
            <Outlet />
          </ConsentProvider>
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const { t } = useTranslation();

  console.error(error);

  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground">
      <div className="w-full max-w-md text-center">
        <h1 className="mb-2 text-xl font-semibold">{t("error.pageTitle")}</h1>
        <p className="mb-6 text-muted-foreground">{t("error.pageBody")}</p>
        <div className="flex flex-wrap justify-center gap-2">
          <Button
            variant="glow"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            {t("action.tryAgain")}
          </Button>
          <a
            className="rounded-md border border-border bg-card px-4 py-2 text-card-foreground"
            href="/"
          >
            {t("action.goHome")}
          </a>
        </div>
      </div>
    </div>
  );
}
